/**
 * THE VENDOR-QUESTION REMOVAL SLOT - reader half (F5, 2026-08-29).
 *
 * MIRROR PAIR: this is the JS twin of backend/routes/_vendor_question_removal
 * .py's reader half (is_removed / removal_reason / kept_entries /
 * removed_entries). The two are pinned by a shared case table,
 * backend/tests/fixtures/vendor_question_removal_parity_cases.json, asserted
 * by BOTH backend/tests/test_vendor_question_removal_parity.py and
 * frontend/src/lib/vendorQuestionRemoval.parity.test.js. Registered in
 * CLAUDE.md's mirror-pair registry in the commit that CREATED the pair.
 * Touch one side, verify the other.
 *
 * ONLY THE READER IS TWINNED. The contract check and the content gate on the
 * Python side are server-side gates with no browser half by design - a rule
 * only the browser enforces is not a rule.
 *
 * WHY BOTH HALVES ARE LOAD-BEARING. The backend's copy decides what enters
 * the edit-signal baseline and what reaches the RFP; this copy decides what
 * the buyer SEES at Review, both the questions offered for editing and the
 * removed ones shown with their reason. If they disagree, the buyer edits a
 * list that is not the list being issued.
 */

// ACCEPTED TRUTHS FOR `removed`, PINNED RATHER THAN LEFT TO TRUTHINESS.
// Boolean("false") is true in JS and bool("false") is True in Python, so a
// bare truthiness test would read the string "false" as a REMOVAL in both
// languages - agreeing with each other while both being wrong. So: the
// boolean true, or the string "true" in any casing. Nothing else - not 1, not
// "yes", and emphatically not "false".
const REMOVED_TRUE_STRINGS = ['true'];

/**
 * True when this revision entry declares the question REMOVED.
 *
 * The default is always "kept": a malformed or unrecognised value must never
 * silently delete a vendor question, because the failure is invisible (the
 * question simply is not there) and there is no restore control by ruling.
 */
export function isRemoved(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
  const value = item.removed;
  if (value === true) return true;
  if (typeof value === 'string') {
    return REMOVED_TRUE_STRINGS.includes(value.trim().toLowerCase());
  }
  return false;
}

/**
 * The buyer-facing reason, trimmed. Empty string when absent or blank.
 *
 * This is what the buyer reads at Review and it is THE ONLY RECORD of why the
 * question is gone - the question itself leaves no footprint anywhere else.
 * The backend makes it mandatory on a removal for exactly that reason.
 *
 * Coerced through String() rather than type-checked so a non-string reason
 * reads the same here as str() makes it read in the Python twin - the exact
 * divergence the category_no_fit table caught on its first run.
 */
export function removalReason(item) {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return '';
  const reason = item.reason;
  if (reason === null || reason === undefined) return '';
  return String(reason).trim();
}

/**
 * The entries that survive to the buyer, the RFP and the edit-signal
 * baseline. Non-object entries are KEPT, not dropped - isRemoved defaults to
 * "kept" and this must agree with it.
 */
export function keptEntries(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.filter((e) => !isRemoved(e));
}

/**
 * The entries the Review surface must show as removed (ruling 3: a question
 * vanishing between generation and review with no trace gets rediscovered as
 * a defect).
 */
export function removedEntries(entries) {
  if (!Array.isArray(entries)) return [];
  return entries.filter((e) => isRemoved(e));
}
